import {Action, createAction, createReducer, on, props} from '@ngrx/store';
import {
  ConfigState,
  Dictionary,
  FinanceState,
  MortgageContract,
  MortgageOverview,
  Payment,
  PaymentOverview,
  PaymentPlan,
  Scenario
} from './AppState';
import {calculateMortgagesAsArray} from '../domain/mortgages.model';
import {MortgageId, PaymentId, ScenarioId} from '../domain/scenario.model';
import {lastScenario} from './utils';


export interface InflationCorrectionEnabled {
  scenarioId: ScenarioId;
}

export interface InflationCorrectionDisabled {
  scenarioId: ScenarioId;
}

export interface InflationRateChanged {
  rate: number;
  scenarioId: ScenarioId;
}

export interface LogViewChanged {
  showLog: boolean;
}

export interface MortgageAdded {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
  contract: MortgageContract;
}

export interface PaymentAdded {
  plan: PaymentPlan;
  mortgageId: MortgageId;
  scenarioId: ScenarioId;
  paymentId: PaymentId;
}

export interface PaymentUpdated {
  plan: PaymentPlan;
  mortgageId: MortgageId;
  scenarioId: ScenarioId;
  paymentId: PaymentId;
}

export interface PaymentRemoved {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
  paymentId: PaymentId;
}

export interface MortgageRemoved {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
}

export interface MortgageUpdated {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
  contract: MortgageContract;
}

export interface InitialScenarioAdded {
  scenario: Scenario;
}

export interface ScenarioCopied {
  scenarioId: ScenarioId;
}

export interface ScenarioRemoved {
  scenarioId: ScenarioId;
}

export interface ActionFailed {
  action: string;
  payload: any;
  reason: string;
}

export const actionFailed = createAction('[Event] (Action Failed)', props<ActionFailed>());

export const actionsLoaded = createAction('[Event] (Actions Loaded)');
export const logViewChanged = createAction('[Event] (Log view Changed)', props<LogViewChanged>());
export const inflationCorrectionEnabled = createAction('[Event] (Inflation Correction Enabled)', props<InflationCorrectionEnabled>());
export const inflationCorrectionDisabled = createAction('[Event] (Inflation Correction Disabled)', props<InflationCorrectionDisabled>());
export const inflationRateChanged = createAction('[Event] (Inflation Rate Changed)', props<InflationRateChanged>());
export const mortgageAdded = createAction('[Event] (Mortgage Added)', props<MortgageAdded>());
export const paymentAdded = createAction('[Event] (Payment Added)', props<PaymentAdded>());
export const paymentUpdated = createAction('[Event] (Payment Updated)', props<PaymentUpdated>());
export const paymentRemoved = createAction('[Event] (Payment Removed)', props<PaymentRemoved>());
export const mortgageRemoved = createAction('[Event] (Mortgage Removed)', props<MortgageRemoved>());
export const mortgageUpdated = createAction('[Event] (Mortgage Updated)', props<MortgageUpdated>());
export const initialScenarioAdded = createAction('[Event] (Initial Scenario Added)', props<InitialScenarioAdded>());
export const scenarioCopied = createAction('[Event] (Scenario Copied)', props<ScenarioCopied>());
export const scenarioRemoved = createAction('[Event] (Scenario Removed)', props<ScenarioRemoved>());


export const initialState: FinanceState = {
  scenarios: {},
  lastScenarioId: null,
  scenarioIds: [],
};

export const initialConfigState: ConfigState = {
  logView: false,
};

const _financeReducer = createReducer(
  initialState,
  on(mortgageUpdated, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const existingPayments = existingScenario.mortgages[event.mortgageId].payments;
      const newTimeline = calculateMortgagesAsArray(event.contract, existingPayments);
      const newMortgage: MortgageOverview = {
        timeline: newTimeline,
        contract: event.contract,
        mortgageId: '0',
        payments: existingPayments,
        lastPaymentId: null
      };

      newMortgage.mortgageId = event.mortgageId;

      const updatedScenario: Scenario = {
        ...existingScenario,
        mortgages: {
          ...existingScenario.mortgages,
          [event.mortgageId]: newMortgage
        },
        mortgageCount: existingScenario.mortgageCount
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),

  on(mortgageAdded, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const newTimeline = calculateMortgagesAsArray(event.contract);
      const newMortgage: MortgageOverview = {
        timeline: newTimeline,
        contract: event.contract,
        mortgageId: '0',
        payments: {},
        lastPaymentId: null
      };

      newMortgage.mortgageId = event.mortgageId;

      const updatedScenario: Scenario = {
        ...existingScenario,
        mortgages: {
          ...existingScenario.mortgages,
          [event.mortgageId]: newMortgage
        },
        mortgageCount: existingScenario.mortgageCount + 1
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),


  on(paymentAdded, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const updatedPayments = {
        ...existingScenario.mortgages[event.mortgageId].payments,
        [event.paymentId]: {
          plan: event.plan,
          paymentId: event.paymentId,
          timeline: [{
            amount: event.plan.amount,
            month: event.plan.startAtMonth
          } as Payment]
        } as PaymentOverview
      };

      const updatedMortgageTimeline = calculateMortgagesAsArray(existingScenario.mortgages[event.mortgageId].contract, updatedPayments);

      const updatedMortgage: MortgageOverview = {
        ...existingScenario.mortgages[event.mortgageId],
        payments: updatedPayments,
        timeline: updatedMortgageTimeline,
        lastPaymentId: event.paymentId
      };


      const updatedScenario: Scenario = {
        ...existingScenario,
        mortgages: {
          ...existingScenario.mortgages,
          [event.mortgageId]: updatedMortgage
        }
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),

  on(paymentUpdated, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const updatedPayments = {
        ...existingScenario
          .mortgages[event.mortgageId]
          .payments,
        [event.paymentId]: {
          plan: event.plan,
          paymentId: event.paymentId,
          timeline: [{
            amount: event.plan.amount,
            month: event.plan.startAtMonth
          } as Payment]
        } as PaymentOverview
      };

      const updatedMortgageTimeline = calculateMortgagesAsArray(existingScenario.mortgages[event.mortgageId].contract, updatedPayments);

      const updatedMortgage: MortgageOverview = {
        ...existingScenario.mortgages[event.mortgageId],
        timeline: updatedMortgageTimeline,
        payments: updatedPayments
      };

      const updatedScenario: Scenario = {
        ...existingScenario,
        mortgages: {
          ...existingScenario.mortgages,
          [event.mortgageId]: updatedMortgage
        }
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),


  on(paymentRemoved, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const updatedPayments = {
        ...existingScenario.mortgages[event.mortgageId].payments
      };

      delete updatedPayments[event.paymentId];


      const updatedMortgageTimeline = calculateMortgagesAsArray(existingScenario.mortgages[event.mortgageId].contract, updatedPayments);

      const updatedMortgage: MortgageOverview = {
        ...existingScenario.mortgages[event.mortgageId],
        timeline: updatedMortgageTimeline,
        payments: updatedPayments
      };


      const updatedScenario: Scenario = {
        ...existingScenario,
        mortgages: {
          ...existingScenario.mortgages,
          [event.mortgageId]: updatedMortgage
        }
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),

  on(mortgageRemoved, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId];
      const updatedMortgages: Dictionary<MortgageOverview> = {
        ...existingScenario.mortgages
      };

      delete updatedMortgages[event.mortgageId];

      const updatedScenario: Scenario = {
        ...existingScenario,
        mortgages: updatedMortgages,
        mortgageCount: existingScenario.mortgageCount - 1
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),

  on(initialScenarioAdded, (state: FinanceState, event) => {
      return {
        ...state,
        scenarios: {
          [event.scenario.id]: event.scenario
        },
        lastScenarioId: event.scenario.id,
        scenarioIds: [event.scenario.id],

      } as FinanceState;
    }
  ),

  on(scenarioCopied, (state: FinanceState, event) => {
      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: {
            ...lastScenario(state)
          }
        },
        lastScenarioId: event.scenarioId,
        scenarioIds: [...state.scenarioIds, event.scenarioId]
      } as FinanceState;
    }
  ),

  on(scenarioRemoved, (state: FinanceState, event) => {
    const scenarios = {...state.scenarios};
    delete scenarios[event.scenarioId];
    const idIndex = state.scenarioIds.indexOf(event.scenarioId, 0);
    const scenarioIds = [...state.scenarioIds];
    scenarioIds.splice(idIndex, 1);
    const lastScenarioId = (scenarioIds.length === 0) ? null : scenarioIds[scenarioIds.length - 1];

    return {
      ...state,
      scenarios: scenarios,
      scenarioIds: scenarioIds,
      lastScenarioId: lastScenarioId,
    } as FinanceState;
  }),

  on(inflationCorrectionEnabled, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const updatedScenario: Scenario = {
        ...existingScenario,
        useInflationCorrection: true,
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),
  on(inflationCorrectionDisabled, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const updatedScenario: Scenario = {
        ...existingScenario,
        useInflationCorrection: false,
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),
  on(inflationRateChanged, (state: FinanceState, event) => {
      const existingScenario: Scenario = state.scenarios[event.scenarioId]; // always exists
      const updatedScenario: Scenario = {
        ...existingScenario,
        inflationRate: event.rate,
      };

      return {
        ...state,
        scenarios: {
          ...state.scenarios,
          [event.scenarioId]: updatedScenario
        }
      } as FinanceState;
    }
  ),
);

const _configReducer = createReducer(
  initialConfigState,
  on(logViewChanged, (state: ConfigState, event) => {
      return {
        ...state,
        logView: event.showLog
      } as ConfigState;
    }
  ),
);

export function reducerEvents(state: FinanceState | undefined, action: Action): FinanceState {
  return _financeReducer(state, action);
}

export function reducerConfigEvents(state: ConfigState | undefined, action: Action): ConfigState {
  return _configReducer(state, action);
}
